import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { MotoIcon } from "./MotoIcon";
import { supabase } from "@/integrations/supabase/client";
import { useAssistantSettings } from "@/hooks/useAssistantSettings";
import { useMotoGPCalendar } from "@/hooks/useMotoGPCalendar";

interface UserProfile {
  name: string | null;
  comunidad: string | null;
  language: string | null;
}

function useUserProfile(): UserProfile {
  const [profile, setProfile] = useState<UserProfile>({ name: null, comunidad: null, language: null });
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("profiles")
        .select("display_name, full_name, comunidad_autonoma, language_preference")
        .eq("user_id", user.id)
        .single();
      if (data) setProfile({
        name: data.display_name || data.full_name || null,
        comunidad: data.comunidad_autonoma || null,
        language: data.language_preference || null,
      });
    })();
  }, []);
  return profile;
}

function getRegionalGreeting(comunidad: string | null, language: string | null, name: string | null): { greeting: string; subtitle: string; hint: string } {
  const displayName = name || "piloto";

  // Language-specific greetings
  if (language === "catalan" || language === "valenciano") {
    return {
      greeting: `Hola ${displayName}! 🏁`,
      subtitle: language === "valenciano"
        ? "Sóc el teu assistent personal de PADDOCK 69. Che, preguntem el que necessites!"
        : "Sóc el teu assistent personal de PADDOCK 69. Pregunta'm el que necessitis!",
      hint: "Pregunta'm sobre GPs, el grup o el que necessitis",
    };
  }
  if (language === "gallego") {
    return {
      greeting: `Ola ${displayName}! 🏁`,
      subtitle: "Son o teu asistente persoal de PADDOCK 69. Pregúntame o que precises, bro!",
      hint: "Pregúntame sobre GPs, o grupo ou o que necesites",
    };
  }
  if (language === "euskera") {
    return {
      greeting: `Kaixo ${displayName}! 🏁`,
      subtitle: "PADDOCK 69ko zure laguntzaile pertsonala naiz. Aupa! Galdetu nahi duzuna!",
      hint: "Galdetu GPei buruz, taldeari buruz edo behar duzunari buruz",
    };
  }

  // Region-specific Spanish greetings
  const regionGreetings: Record<string, { greeting: string; subtitle: string }> = {
    andalucia: { greeting: `¡Quillo ${displayName}! 🏁`, subtitle: "Zoy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que te haga farta!" },
    cataluna: { greeting: `¡Hola ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que necesites, crack!" },
    valencia: { greeting: `¡Che ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que necesites!" },
    galicia: { greeting: `¡Hola ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. Pregúntame lo que precises, bro." },
    pais_vasco: { greeting: `¡Aupa ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que necesites!" },
    madrid: { greeting: `¡Hola ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que te haga falta, crack!" },
    aragon: { greeting: `¡Hola ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que necesites, ñ maño!" },
    navarra: { greeting: `¡Aupa ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que necesites!" },
    murcia: { greeting: `¡Hola ${displayName}! 🏁`, subtitle: "Soy tu asistente personal de PADDOCK 69. ¡Pregúntame lo que te haga falta, illo!" },
  };

  const regional = comunidad ? regionGreetings[comunidad] : null;
  return {
    greeting: regional?.greeting || `¡Hola ${displayName}! 🏁`,
    subtitle: regional?.subtitle || "Soy tu asistente personal de PADDOCK 69.",
    hint: "Pregúntame sobre GPs, el grupo o lo que necesites",
  };
}

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface PitStopChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pit-stop-chat`;

export function PitStopChat({ isOpen, onClose }: PitStopChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { settings: assistantSettings, getAvatarEmoji } = useAssistantSettings();
  const userProfile = useUserProfile();
  const userName = userProfile.name;
  const { nextRace, countdown } = useMotoGPCalendar();
  const regionalGreeting = getRegionalGreeting(userProfile.comunidad, userProfile.language, userName);

  const seasonLine = nextRace
    ? `⏱️ ${countdown.label} ${nextRace.flag || "🏁"}`
    : "🏁 ¡Temporada 2026 finalizada! Nos vemos en 2027";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const streamChat = async (userMessage: string) => {
    const newMessages: Message[] = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      // Get the user's session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Sesión expirada", {
          description: "Por favor, vuelve a iniciar sesión",
        });
        setIsLoading(false);
        return;
      }

      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ messages: newMessages }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          toast.error("🏁 Pit-Stop sobrecargado", {
            description: "Demasiadas consultas. Espera un momento.",
          });
        } else if (response.status === 402) {
          toast.error("⛽ Depósito vacío", {
            description: "El combustible de IA se agotó.",
          });
        } else {
          toast.error("Error en Pit-Stop", {
            description: errorData.message || "Algo falló en el box",
          });
        }
        setIsLoading(false);
        return;
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      let textBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });
        
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);
          
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;
          
          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;
          
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setMessages([
                ...newMessages,
                { role: "assistant", content: assistantContent },
              ]);
            }
          } catch {
            // Put back incomplete JSON
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Final flush
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setMessages([
                ...newMessages,
                { role: "assistant", content: assistantContent },
              ]);
            }
          } catch {
            /* ignore partial leftovers */
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Error de conexión", {
        description: "No se pudo conectar con Pit-Stop",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    const message = input.trim();
    setInput("");
    streamChat(message);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-24 left-4 w-[calc(100%-2rem)] max-w-sm z-50"
        >
          <div className="card-telemetry overflow-hidden shadow-neon-strong border-primary/30">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-border bg-card/80">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8">
                  <MotoIcon isLoading={isLoading} />
                </div>
                <div>
                  <h3 className="font-mono font-bold text-sm text-primary">
                    {getAvatarEmoji(assistantSettings.assistant_avatar)} {assistantSettings.assistant_name}
                  </h3>
                  <p className="text-[10px] text-muted-foreground">
                    Asistente PADDOCK 69
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea 
              ref={scrollRef}
              className="h-72 p-3"
            >
              {messages.length === 0 && (
                <div className="flex items-start gap-2 p-2">
                  <span className="text-2xl shrink-0 mt-0.5">{getAvatarEmoji(assistantSettings.assistant_avatar)}</span>
                  <div className="bg-secondary rounded-lg px-3 py-2 text-sm text-foreground space-y-1">
                    <p className="font-semibold text-primary">
                      {regionalGreeting.greeting}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      <span className="font-bold text-foreground">{assistantSettings.assistant_name}</span> — {regionalGreeting.subtitle}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {seasonLine}
                    </p>
                    <p className="text-muted-foreground text-[10px] mt-1">
                      {regionalGreeting.hint}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-foreground"
                      }`}
                    >
                      {message.role === "assistant" ? (
                        <div className="prose prose-sm prose-invert max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm [&_strong]:text-primary [&_a]:text-primary">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      ) : (
                        message.content
                      )}
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && messages[messages.length - 1]?.role === "user" && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-secondary rounded-lg px-3 py-2 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-xs text-muted-foreground">
                        Motor arrancando...
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-3 border-t border-border">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 h-9 text-sm bg-secondary border-border"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  size="icon"
                  className="h-9 w-9 btn-racing"
                  disabled={isLoading || !input.trim()}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
